import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { 
  Search, Filter, KanbanSquare, List as ListIcon, 
  ChevronDown, Plus
} from "lucide-react";
import { cn } from "../../components/ui/utils";
import { Ticket } from "../../components/service-desk/TicketCard";
import { TicketKanbanView } from "../../components/service-desk/TicketKanbanView";
import { TicketListView } from "../../components/service-desk/TicketListView";
import { TicketFilterBar } from "../../components/service-desk/TicketFilterBar";
import { toast } from "sonner";
import api from "../../utils/api";
import { serviceDeskApi } from "../../utils/service-desk-api";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { ResolutionDetailsModal } from "../../components/service-desk/ResolutionDetailsModal";

import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "../../components/ui/dropdown-menu";

const STATUS_CONFIG = {
  NEW: { label: "New Request", color: "bg-blue-50 text-blue-700 border-blue-100", kanbanColor: "bg-gray-50" },
  TODO: { label: "To Do", color: "bg-gray-100 text-gray-700 border-gray-200", kanbanColor: "bg-blue-50/50 border-blue-100" },
  DOING: { label: "Doing", color: "bg-orange-50 text-orange-700 border-orange-200", kanbanColor: "bg-orange-50/50 border-orange-100" },
  BLOCKED: { label: "Blocked", color: "bg-red-50 text-red-700 border-red-200", kanbanColor: "bg-red-50/50 border-red-100" },
  RESOLVED: { label: "Resolved", color: "bg-green-50 text-green-700 border-green-200", kanbanColor: "bg-green-50/50 border-green-100" },
  CLOSED: { label: "Closed", color: "bg-gray-100 text-gray-500 border-gray-200", kanbanColor: "bg-gray-100" },
};

const PRIORITY_CONFIG = {
  Urgent: "text-red-600 bg-red-50 border-red-200",
  High: "text-orange-600 bg-orange-50 border-orange-200",
  Medium: "text-blue-600 bg-blue-50 border-blue-200",
  Low: "text-gray-600 bg-gray-50 border-gray-200",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  
  const [view, setView] = useState<'kanban' | 'list'>('kanban');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [serviceDesks, setServiceDesks] = useState<any[]>([]);
  const [selectedDeskId, setSelectedDeskId] = useState<string>(queryParams.get('deskId') || "all");
  const [selectedStatus, setSelectedStatus] = useState<string>(queryParams.get('status') || "all");
  const [selectedPriority, setSelectedPriority] = useState<string>(queryParams.get('priority') || "all");
  const [selectedAssignee, setSelectedAssignee] = useState<string>(queryParams.get('assignee') || "all");
  const [searchQuery, setSearchQuery] = useState<string>(queryParams.get('search') || "");
  const [isLoading, setIsLoading] = useState(true);
  const [isResolutionModalOpen, setIsResolutionModalOpen] = useState(false);
  const [resolvingTicket, setResolvingTicket] = useState<Ticket | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const ability = React.useContext(AbilityContext);

  const isGlobalAdmin = ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_ADMIN);
  const isDeskStaff = ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_TEAM_ADMIN) ||
                      ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_AGENT) ||
                      ability.can(ACTIONS.READ, RESOURCES.SERVICE_DESK_AGENT_VIEWER);

  const canSubmit = ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_REQUESTER) || 
                    ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_ADMIN) || 
                    ability.can(ACTIONS.MANAGE, RESOURCES.SERVICE_DESK_TEAM_ADMIN);

  useEffect(() => {
    // If they aren't even desk staff, they definitely can't be here
    if (!isGlobalAdmin && !isDeskStaff) {
      toast.error("You are not authorized to access the Service Desk Admin Dashboard.");
      navigate('/my-requests');
      return;
    }

    // If they are desk staff but not global admin, and we have a specific deskId, 
    // redirect them to the specific desk view which they are more likely to have access to.
    if (!isGlobalAdmin && isDeskStaff && queryParams.get('deskId')) {
      const deskId = queryParams.get('deskId');
      const assignee = queryParams.get('assignee');
      navigate(`/service-desk/${deskId}${assignee ? `?assignee=${assignee}` : ''}`);
      return;
    }

    fetchData();
  }, [isGlobalAdmin, isDeskStaff, navigate, location.search]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [desksRes, ticketsRes] = await Promise.all([
        serviceDeskApi.getServiceDesks({ retired: false }),
        api.get('/api/service-desk/tickets') 
      ]);

      if (desksRes.success) {
        setServiceDesks(desksRes.desks);
      }

      if (ticketsRes.success) {
         const mappedTickets: Ticket[] = ticketsRes.tickets.map((t: any) => {
            const createdAt = new Date(t.createdAt);
            const now = new Date();
            const diffHours = (now.getTime() - createdAt.getTime()) / (1000 * 60 * 60);
            const slaHours = t.formVersion?.requestForm?.serviceDesk?.slaHours || 48;
            const isBreached = diffHours > slaHours && !['RESOLVED', 'CLOSED'].includes(t.status);

            const getInitials = (name: string) => name?.split(' ').map((n: string) => n[0]).join('').toUpperCase() || '?';
            const colors = ['bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-pink-500', 'bg-orange-500', 'bg-indigo-500'];
            const getUserColor = (userId: any) => colors[parseInt(userId) % colors.length] || 'bg-gray-500';

            return {
              id: t.id.toString(),
              formattedId: `REQ-${t.id.toString().padStart(3, '0')}`,
              title: t.title,
              category: t.formVersion?.requestForm?.name || 'General',
              serviceDesk: t.formVersion?.requestForm?.serviceDesk?.name || 'Unknown',
              serviceDeskId: t.formVersion?.requestForm?.serviceDesk?.id?.toString(),
              status: t.status,
              priority: (t.priority.charAt(0).toUpperCase() + t.priority.slice(1).toLowerCase()) as any,
              submitter: {
                name: t.requester?.name || 'Unknown',
                initials: getInitials(t.requester?.name),
                email: t.requester?.email || '',
                color: getUserColor(t.requester?.id)
              },
              assignees: (t.assignees || []).map((u: any) => ({
                name: u.name,
                initials: getInitials(u.name),
                email: u.email,
                color: getUserColor(u.id)
              })),
              createdAt: t.createdAt,
              isBreached,
              comments: (t.comments || []).filter((c: any) => !c.isPrivate).length,
              attachments: t.attachments?.length || 0,
              tags: t.data?.tags || []
            };
          });
          setTickets(mappedTickets);
      }
    } catch (error) {
      console.error("Error fetching admin data:", error);
      toast.error("Failed to load dashboard data.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleTicketMove = async (ticketId: string, newStatus: string) => {
    const ticket = tickets.find(t => t.id === ticketId);
    if (!ticket || ticket.status === newStatus) return;

    if (newStatus === 'RESOLVED') {
      setResolvingTicket(ticket);
      setIsResolutionModalOpen(true);
      return;
    }

    // Capture original status for rollback
    const originalStatus = ticket.status;

    // Optimistically update UI
    setTickets(prev => prev.map(t => 
      t.id === ticketId ? { ...t, status: newStatus } : t
    ));

    try {
      const response = await api.patch(`/api/service-desk/requests/${ticketId}/status`, { status: newStatus });
      if (!response.success) {
        throw new Error(response.message || "Failed to update status");
      }
      // No toast on success for seamless feel, unless preferred
    } catch (error) {
      console.error("Error updating ticket status:", error);
      toast.error("Failed to update ticket status. Reverting change.");
      
      // Rollback to original status
      setTickets(prev => prev.map(t => 
        t.id === ticketId ? { ...t, status: originalStatus } : t
      ));
    }
  };

  const handleResolutionSubmit = async (details: any) => {
    if (!resolvingTicket) return;
    
    setIsUpdatingStatus(true);
    try {
      const response = await api.patch(`/api/service-desk/requests/${resolvingTicket.id}/status`, { 
        status: 'RESOLVED',
        resolutionDetails: details
      });

      if (response.success) {
        setTickets(prev => prev.map(t => 
          t.id === resolvingTicket.id ? { ...t, status: 'RESOLVED' } : t
        ));
        setIsResolutionModalOpen(false);
        setResolvingTicket(null);
        toast.success("Ticket resolved successfully");
      }
    } catch (error) {
      console.error("Error resolving ticket:", error);
      toast.error("Failed to resolve ticket");
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // Backend-fed assignee options for the filter: unique agent names across loaded tickets.
  const assigneeNames = useMemo(() => {
    const names = new Set(
      tickets.flatMap((t) => t.assignees?.map((u) => u.name) || []).filter(Boolean)
    );
    return Array.from(names).sort() as string[];
  }, [tickets]);

  const filteredTickets = tickets.filter(req => {
    if (selectedDeskId !== "all" && (req as any).serviceDeskId !== selectedDeskId) return false;
    if (selectedStatus !== "all" && req.status !== selectedStatus) return false;
    if (selectedPriority !== "all" && req.priority !== selectedPriority) return false;
    if (selectedAssignee !== "all") {
        if (selectedAssignee === "unassigned") {
            if (req.assignees && req.assignees.length > 0) return false;
        } else if (!(req.assignees || []).some(u => u.name === selectedAssignee)) {
            return false;
        }
    }
    if (searchQuery.trim() !== "") {
        const query = searchQuery.toLowerCase();
        return req.title.toLowerCase().includes(query) || req.id.toLowerCase().includes(query);
    }
    return true;
  });

  return (
    <div className="space-y-6 pb-10 md:pb-0">
      <div className="flex justify-between items-center sm:items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold">Admin Dashboard</h1>
          <p className="text-sm text-muted-foreground">View and manage all requests across service desks</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Desktop View Switcher & Action */}
          <div className="hidden sm:flex items-center gap-3">
            <div className="flex items-center bg-gray-100 p-1 rounded-md">
              <Button 
                variant={view === 'kanban' ? "secondary" : "ghost"} 
                size="sm" 
                className={cn("rounded-sm h-8", view === 'kanban' && "bg-white shadow-sm")}
                onClick={() => setView('kanban')}
              >
                <KanbanSquare className="mr-2 h-4 w-4" /> Kanban
              </Button>
              <Button 
                variant={view === 'list' ? "secondary" : "ghost"} 
                size="sm" 
                className={cn("rounded-sm h-8", view === 'list' && "bg-white shadow-sm")}
                onClick={() => setView('list')}
              >
                <ListIcon className="mr-2 h-4 w-4" /> List
              </Button>
            </div>
            {canSubmit && (
              <Button 
                  className="h-9 font-bold"
                  onClick={() => navigate('/my-requests/new')}
              >
                  <Plus className="mr-2 h-4 w-4" /> New Request
              </Button>
            )}
          </div>

          {/* Mobile Action */}
          {canSubmit && (
            <Button 
              size="icon"
              className="sm:hidden"
              onClick={() => navigate('/my-requests/new')}
            >
                <Plus className="h-5 w-5" />
            </Button>
          )}
        </div>
      </div>

      {/* Mobile View Selector */}
      <div className="sm:hidden w-full mb-6">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="w-full justify-between bg-gray-50 border-gray-200 h-11 rounded-xl">
              <div className="flex items-center gap-2">
                {view === 'kanban' ? <KanbanSquare size={18} className="text-slate-500" /> : <ListIcon size={18} className="text-slate-500" />}
                <span className="font-medium text-gray-900 capitalize">
                  {view} View
                </span>
              </div>
              <ChevronDown size={18} className="text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] p-2 space-y-1">
            <DropdownMenuItem onClick={() => setView('kanban')} className="rounded-lg h-11 font-medium gap-3">
              <KanbanSquare size={18} className="text-blue-600" /> Kanban View
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setView('list')} className="rounded-lg h-11 font-medium gap-3">
              <ListIcon size={18} className="text-blue-600" /> List View
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Stats - Grid on mobile, Flex on desktop */}
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
        <div className="bg-gray-100/50 rounded-lg p-3 border border-gray-100 shadow-sm sm:w-fit">
          <p className="text-[10px] text-gray-500 font-bold uppercase tracking-tight">Total Requests</p>
          <p className="text-2xl font-bold mt-0.5">{tickets.length}</p>
        </div>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <div key={key} className={cn("rounded-lg p-3 border shadow-sm sm:w-fit", config.color)}>
            <p className={cn("text-[10px] font-bold uppercase tracking-tight opacity-70")}>{config.label}</p>
            <p className={cn("text-2xl font-bold mt-0.5")}>{tickets.filter(t => t.status === key).length}</p>
          </div>
        ))}
      </div>

      {/* Filter Bar */}
      <TicketFilterBar 
        serviceDesks={serviceDesks}
        selectedDeskId={selectedDeskId}
        onDeskChange={setSelectedDeskId}
        statusConfig={STATUS_CONFIG}
        selectedStatus={selectedStatus}
        onStatusChange={setSelectedStatus}
        priorityConfig={PRIORITY_CONFIG}
        selectedPriority={selectedPriority}
        onPriorityChange={setSelectedPriority}
        selectedAssignee={selectedAssignee}
        onAssigneeChange={setSelectedAssignee}
        assignees={assigneeNames}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      {/* View Content */}
      {view === 'list' ? (
        <TicketListView 
          tickets={filteredTickets} 
          statusConfig={STATUS_CONFIG} 
          isLoading={isLoading}
          onTicketClick={(ticket) => navigate(`/service-desk/view/${ticket.id}`)}
        />
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <p className="text-sm text-muted-foreground animate-pulse">Loading dashboard...</p>
        </div>
      ) : (
        <TicketKanbanView 
          tickets={filteredTickets} 
          statusConfig={STATUS_CONFIG} 
          onTicketMove={handleTicketMove}
          onTicketClick={(ticket) => navigate(`/service-desk/view/${ticket.id}`)}
        />
      )}

      <ResolutionDetailsModal 
        isOpen={isResolutionModalOpen}
        onOpenChange={setIsResolutionModalOpen}
        onSubmit={handleResolutionSubmit}
        requesterName={resolvingTicket?.submitter?.name}
        isSubmitting={isUpdatingStatus}
      />
    </div>
  );
}
